import { type JSX, useCallback } from 'react';

import { Grid, GridItem } from '@plurall/elo-v3';
import { createFileRoute } from '@tanstack/react-router';

import { PageContent } from '@/components';
import { DashboardHeader, Anchors } from '@/features/home-manager';
import { BannerWrapper } from '@/features/home-manager/components/Banner/BannerWrapper';
import { ClassesList } from '@/features/home-manager/components/ClassesListComponent/ClassesList';
import { Engagement } from '@/features/home-manager/components/Engagement/Engagement';
import { FeaturedThemes } from '@/features/home-manager/components/FeaturedThemes/FeaturedThemes';
import { NextSteps } from '@/features/home-manager/components/NextSteps';
import { SchoolClassesEngagement } from '@/features/home-manager/components/SchoolClassesEngagement/SchoolClassesEngagement';
import { StudentsList } from '@/features/home-manager/components/StudentsListComponent/StudentsList';
import { SchoolProvider } from '@/features/home-manager/context/schoolStore';
import {
  coordenadorFiltersSchema,
  type CoordenadorUrlFilters,
} from '@/features/home-manager/lib/coordenadorFilters.schema';
import { useNavigateMerge } from '@/hooks/useNavigateMerge';
import { requireAccess } from '@/permissions';

export const Route = createFileRoute('/home/coordenador')({
  staticData: { title: 'Coordenador' },
  component: CoordenadorRoute,
  beforeLoad: requireAccess('view:manager_dashboard'),
  validateSearch: (search: Record<string, unknown>): CoordenadorUrlFilters => coordenadorFiltersSchema.parse(search),
});

function CoordenadorRoute(): JSX.Element {
  const { escola } = Route.useSearch();
  const navigate = useNavigateMerge();

  const handleSchoolIdChange = useCallback(
    (schoolId: string) => {
      void navigate({ search: { escola: Number(schoolId) }, replace: true });
    },
    [navigate]
  );

  return (
    <SchoolProvider
      schoolId={escola !== undefined ? escola.toString() : null}
      onSchoolIdChange={handleSchoolIdChange}>
      <DashboardHeader />
      <PageContent>
        <Grid
          as='section'
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
          gap='space.16'
          marginBlockEnd='space.24'
          css={{ '& > *': { minWidth: 0 } }}>
          <Anchors />
        </Grid>

        <Grid
          as='section'
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
          gap='space.16'
          autoFlow='row dense'
          marginBlockEnd='space.16'
          css={{ '& > *': { minWidth: 0 } }}>
          <GridItem
            height={'100%'}
            colSpan={{ base: 1 }}>
            <Engagement />
          </GridItem>

          <GridItem
            height={'100%'}
            colSpan={{ md: 1, xl: 3 }}
            gridRow={{ mdDown: 1 }}
            css={{ '& > *': { height: '100%' } }}>
            <BannerWrapper />
          </GridItem>

          <GridItem
            height={'100%'}
            colSpan={{ md: 2 }}
            display='flex'
            flexDirection='column'
            css={{ '& > *': { height: '100%' } }}>
            <NextSteps />
          </GridItem>

          <GridItem
            colSpan={{ md: 2 }}
            rowSpan={{ xl: 2 }}
            height='615px'
            overflow='hidden'
            css={{ '& > *': { height: '100%' } }}>
            <FeaturedThemes />
          </GridItem>

          <GridItem
            height={'100%'}
            colSpan={{ base: 1 }}
            css={{ '& > *': { height: '100%' } }}>
            <ClassesList />
          </GridItem>

          <GridItem
            height={'100%'}
            colSpan={{ base: 1 }}
            css={{ '& > *': { height: '100%' } }}>
            <StudentsList />
          </GridItem>

          <GridItem
            colSpan={{ md: 2, xl: 4 }}
            overflow='hidden'>
            <SchoolClassesEngagement />
          </GridItem>
        </Grid>
      </PageContent>
    </SchoolProvider>
  );
}
